import React from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';

import Card from '@app/components/UI/Card/Card';
import Tabs from '@app/components/UI/Tabs/Tabs';
import GalleryPrint from '@app/imgs/GalleryPrint';
import NextArrow from '@app/imgs/NextArrow';
import PrevArrow from '@app/imgs/PrevArrow';

interface ChildrenBookProps {
	title: string;
	shortTitle: string;
	img: string;
	data: string;
	amazon: string | null;
	desc: React.ReactNode;
	inProcess?: boolean;
}

const booksChildren: Array<ChildrenBookProps> = [
	{
		title: 'Darla Dolphin Flips out',
		shortTitle: 'Darla Dolphin Flips out',
		img: '/images/darla-dolphin-flips-out.png',
		data: 'COMING MARCH 2024!',
		amazon: null,
		desc: (
			<>
				<p>
					Darla Dolphin swims away in search of new friends, but what she finds is an emergency situation—right in the middle of her
					beautiful blue ocean. Join Darla as she works at lightning speed to rescue a group of frightened sea creatures. This story is
					perfect for teaching children the importance of finding their own strength and embracing their individuality.
				</p>
			</>
		),
		inProcess: true,
	},
	{
		title: 'Adoption Day at the Shelter',
		shortTitle: 'Adoption Day at the Shelter',
		img: '/images/covercat.png',
		data: 'COMING SUMMER 2024!',
		amazon: null,
		desc: (
			<>
				<p>
					Allow me to introduce you to Abner and Adlyn, a duo of elderly yet profoundly wise and affectionate companions, patiently passing
					their days at the animal shelter. Amidst the backdrop of adorable puppies and kittens, their quest to discover a permanent home
					unfolds on Adoption Day.
				</p>
			</>
		),
		inProcess: true,
	},
	{
		title: 'Snorrin’ Norrin',
		shortTitle: 'Snorrin’ Norrin',
		img: '/images/coverdog.png',
		data: 'COMING WINTER 2024!',
		amazon: null,
		desc: (
			<>
				<p>
					Meet Norrin, the lovable, newly adopted dog whose thunderous snores disrupt the whole family. Can they find a way to keep Norrin
					close without sacrificing sleep? Join the adventure in "Snorrin' Norrin" to discover the heartwarming solution that brings
					everyone together.
				</p>
			</>
		),
		inProcess: true,
	},
];

const ChildrenBook: React.FC<{ book: ChildrenBookProps }> = ({ book }) => {
	return (
		<div className="book text-secondary">
			<Card
				image={
					<>
						<img src={book.img} alt={`Book - ${book.title}`} />
					</>
				}
				text={
					<>
						<div className="h2">{book.title}</div>
						<div className="rating-block">
							<div className="h3">{book.data}</div>
							{book.amazon && (
								<div>
									<a href={book.amazon} target="_blank" rel="nofollow noreferrer">
										<img src="/images/amazon.png" alt="amazon" />
									</a>
								</div>
							)}
						</div>
						<div className="description">
							{book.desc}
						</div>
					</>
				}
			/>
		</div>
	);
};

const ChildrenBooks = () => {
	const [swiper, setSwiper] = React.useState<any>();
	const [activeSlide, setActiveSlide] = React.useState<string>('0');
	const [mobile, setMobile] = React.useState<boolean>(false);

	const nextSlide = () => {
		if (+activeSlide < booksChildren.length - 1) {
			setActiveSlide(String(+activeSlide + 1));
		}
	};

	const prevSlide = () => {
		if (+activeSlide > 0) {
			setActiveSlide(String(+activeSlide - 1));
		}
	};

	React.useEffect(() => {
		const mobileScreen = window.matchMedia('screen and (max-width: 1100px)');
		const listenerMobile = () => {
			if (mobileScreen.matches) {
				setMobile(true);
			} else {
				setMobile(false);
			}
		};
		listenerMobile();
		mobileScreen.addEventListener('change', listenerMobile);
	}, []);

	return (
		<div id="children-books" className="children-books scroll-landing">
			<div className="container">
				<h2 className="landing-page-h2">Children’s Books:</h2>
				<div className="books">
					{!mobile ? (
						<>
							<div className="pagination is-relative">
								<PrevArrow
									onClick={() => {
										swiper?.slidePrev();
										prevSlide();
									}}
									className={`prev ${activeSlide === '0' ? 'disabled' : ''}`}
								/>
								<NextArrow
									onClick={() => {
										swiper?.slideNext();
										nextSlide();
									}}
									className={`next ${activeSlide === String(booksChildren.length - 1) ? 'disabled' : ''}`}
								/>
							</div>
							<Swiper
								onSwiper={setSwiper}
								spaceBetween={50}
								slidesPerView={3}
								defaultValue={+activeSlide}
								slideToClickedSlide
								centeredSlidesBounds
								onSlideChange={(swiper) => setActiveSlide(String(swiper?.activeIndex))}
								className="children-swiper"
							>
								{booksChildren.map((book, index) => {
									return (
										<SwiperSlide
											key={index}
											onClick={() => {
												setActiveSlide(String(index));
												swiper?.slideTo(index);
											}}
											className={`${index === +activeSlide ? 'active' : ''}`}
										>
											<div className="slide-children">
												<div className="is-relative">
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="452"
														height="397"
														viewBox="0 0 452 397"
														fill="none"
													>
														<path
															d="M442.419 68.7888C480.827 140.96 387.689 282.428 336.319 344.14C303.073 384.079 268.18 416.07 168.149 381.269C-48.9577 305.736 -1.27509
        																145.607 22.6228 73.0445C46.5207 0.481748 106.618 -11.0724 177.882 10.5212C234.893 27.796 279.268 28.0787 294.329 26.0607C327.689 10.232 404.011 -3.38273 442.419 68.7888Z"
															fill="#FFDD60"
														/>
													</svg>
													<img src={book.img} alt={`Book cover - ${book.title}`} />
												</div>
												<div>
													<p className="h4">{book.title}</p>
													{book?.inProcess && <p className="btn btn-primary">In Process</p>}
												</div>
											</div>
										</SwiperSlide>
									);
								})}
							</Swiper>
							<ChildrenBook book={booksChildren[activeSlide]} />
						</>
					) : (
						<Tabs
							defaultKey={String(activeSlide)}
							items={booksChildren.map((book, i) => {
								return {
									key: String(i),
									label: book.shortTitle,
									children: <ChildrenBook key={i} book={book} />,
								};
							})}
							onTabChange={setActiveSlide}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default ChildrenBooks;
